import { useMemo } from 'react';
import { useParams, Navigate } from 'react-router-dom';

import { FullScreenLoading } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';
import { usePageTitle } from '@backrs_ui/grow-ui/src/hooks/usePageTitle';
import { useGetCoachesQuery } from '@backrs_ui/grow-ui/src/redux/coaches/coaches.api';
import { useGetDeepDiveByIdQuery } from '@backrs_ui/grow-ui/src/redux/deepDive/deepDive.api';
import {
  selectIsFeatureFlagEnabled,
  FEATURE_FLAGS,
} from '@backrs_ui/grow-ui/src/redux/featureFlags/featureFlags';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';

import { DeepDiveDetailPage } from './DeepDiveDetailPage';
import { useAgentContext } from './DeepDiveDetailPageContainer.agentContext';

export const DeepDiveDetailPageContainer = () => {
  const flagValues = useAppSelector((state) => state.featureFlags.flagValues);
  const isCoachingEnabled = useAppSelector(selectIsFeatureFlagEnabled(FEATURE_FLAGS.COACHING));

  const { deepDiveId } = useParams<{ deepDiveId: string }>();

  const deepDiveIdNumber = deepDiveId ? parseInt(deepDiveId, 10) : NaN;
  const isValidId = !isNaN(deepDiveIdNumber);

  const {
    data: deepDive,
    isLoading,
    isError,
  } = useGetDeepDiveByIdQuery(deepDiveIdNumber, {
    skip: !isValidId,
  });

  const { data: allCoaches, isLoading: isCoachesLoading } = useGetCoachesQuery(undefined);

  usePageTitle(deepDive?.name);

  const coachesForDeepDive = useMemo(() => {
    if (!allCoaches || !deepDive) return [];

    return allCoaches.filter((coach) => coach.deep_dives?.some((dd) => dd.id === deepDive.id));
  }, [allCoaches, deepDive]);

  useAgentContext(deepDive, coachesForDeepDive);

  if (!(FEATURE_FLAGS.COACHING in flagValues) || isLoading) {
    return <FullScreenLoading />;
  }

  if (!isCoachingEnabled) {
    return <Navigate to={APP_ROUTE.HOME} replace />;
  }

  if (isError || !deepDive || !isValidId) {
    return <Navigate to={APP_ROUTE.DEEP_DIVES} replace />;
  }

  return (
    <DeepDiveDetailPage
      deepDive={deepDive}
      coaches={coachesForDeepDive}
      isCoachesLoading={isCoachesLoading}
    />
  );
};
